// src/pages/AuthPage.js
import React, { useMemo, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import GoogleSignInButton from "../components/GoogleSignInButton";
import {
  FiMail,
  FiLock,
  FiUser,
  FiPhone,
  FiArrowRight,
  FiAlertCircle,
  FiEye,
  FiEyeOff,
  FiArrowLeft,
  FiShield,
  FiCheck,
} from "react-icons/fi";
import { normalizeNigerPhone } from "../utils/nigerPhone";

const AuthPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { login, signup } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const searchParams = new URLSearchParams(location.search);
  const signupMode = searchParams.get("mode") === "signup";
  const from = location.state?.from
    ? location.state.from.pathname + location.state.from.search
    : "/";
  const resetMessage = location.state?.passwordResetMessage || "";

  const [isLogin, setIsLogin] = useState(!signupMode);
  const [loading, setLoading] = useState(false);
  const [googleError, setGoogleError] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    password1: "",
    password2: "",
  });

  const bannerMessage = googleError || errors.general || resetMessage;
  const bannerIsError = Boolean(googleError || errors.general);

  const pageTitle = useMemo(
    () => (isLogin ? t("authPage.login", "Sign In") : t("authPage.signUp", "Create Account")),
    [isLogin, t]
  );

  const pageSubtitle = useMemo(
    () =>
      isLogin
        ? t("authPage.signInContinue", "Enter your credentials to access your account.")
        : t("authPage.joinMarketplace", "Join Niger's trusted marketplace for buyers & sellers."),
    [isLogin, t]
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const nextValue =
      name === "phone_number" ? normalizeNigerPhone(value) : value;

    setFormData((prev) => ({
      ...prev,
      [name]: nextValue,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: undefined,
      general: undefined,
    }));

    setGoogleError("");
  };

  const switchMode = (mode) => {
    const nextIsLogin = mode === "login";
    setIsLogin(nextIsLogin);
    setErrors({});
    setGoogleError("");
    setAgreedToTerms(false);
    setFormData({
      first_name: "",
      last_name: "",
      email: "",
      phone_number: "",
      password1: "",
      password2: "",
    });

    navigate(`/login${nextIsLogin ? "" : "?mode=signup"}`, { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});
    setGoogleError("");

    if (!isLogin && formData.password1 !== formData.password2) {
      setErrors({ password2: t("authPage.passwordsNoMatch", "Passwords do not match.") });
      setLoading(false);
      return;
    }

    if (!isLogin && !agreedToTerms) {
      setErrors({ terms: t("authPage.requiredTerms", "You must agree to the Terms of Service to continue.") });
      setLoading(false);
      return;
    }

    try {
      if (isLogin) {
        const user = await login(formData.email, formData.password1);
        navigate(user?.is_first_login ? "/onboarding" : from, {
          replace: true,
        });
      } else {
        const signupFormData = new FormData();
        const phoneNumber = normalizeNigerPhone(formData.phone_number);
        Object.entries(formData).forEach(([key, value]) => {
          if (key === "phone_number") {
            if (phoneNumber) {
              signupFormData.append(key, phoneNumber);
            }
            return;
          }
          if (value !== null && value !== undefined) {
            signupFormData.append(key, value);
          }
        });

        await signup(signupFormData);
        navigate("/verify-otp", {
          state: { email: formData.email },
          replace: true,
        });
      }
    } catch (err) {
      if (err.response?.data) {
        const backendErrors = {};
        for (const key in err.response.data) {
          const message = Array.isArray(err.response.data[key])
            ? err.response.data[key][0]
            : err.response.data[key];
          backendErrors[key] = message;
        }
        setErrors(backendErrors);
      } else {
        setErrors({
          general: err.message || "Something went wrong. Please try again.",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const renderFieldError = (name) =>
    errors[name] ? (
      <p className="mt-1 text-[11px] text-red-600 font-medium flex items-center gap-1">
        <FiAlertCircle size={12} className="shrink-0" /> {errors[name]}
      </p>
    ) : null;

  return (
    <div className="min-h-screen relative flex flex-col justify-between overflow-x-hidden select-none text-[#1d1d1f]">
      {/* ── 1. FULL-VIEWPORT LIGHT HD CINEMATIC BACKGROUND ── */}
      <div className="fixed inset-0 w-full h-full -z-10 overflow-hidden">
        <img
          src="/auth-hd-light-bg.jpg"
          alt="Chafadi Sunlit Flagship"
          className="w-full h-full object-cover transform scale-105 transition-transform duration-1000 ease-out"
        />
        {/* Luminous light scrim for crisp legibility and Apple-like brightness */}
        <div className="absolute inset-0 bg-white/45 backdrop-blur-[2.5px]" />
        <div className="absolute inset-0 bg-gradient-to-t from-white/95 via-white/35 to-white/60" />
        <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 via-transparent to-orange-500/10 pointer-events-none" />
      </div>

      {/* ── 2. TOP NAV BAR ── */}
      <header className="relative z-10 w-full max-w-5xl mx-auto px-4 sm:px-6 pt-5 sm:pt-6 flex items-center justify-between">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-[12px] sm:text-[13px] font-semibold text-gray-700 hover:text-[var(--color-accent)] transition-all px-3.5 py-1.5 rounded-full bg-white/80 hover:bg-white backdrop-blur-md border border-black/10 shadow-sm"
        >
          <FiArrowLeft size={14} />
          <span>{t("common.back", "Store")}</span>
        </Link>

        <Link to="/" className="inline-flex items-center transition-transform hover:scale-105 duration-300">
          <img
            src="/logo.png"
            alt="Chafadi"
            className="h-8 sm:h-9 w-auto object-contain drop-shadow-sm"
          />
        </Link>

        <div className="flex items-center gap-2">
          <span className="px-3 py-1.5 rounded-full bg-white/80 backdrop-blur-md border border-black/10 text-[11px] font-semibold text-gray-700 shadow-sm flex items-center gap-1.5">
            <span className="text-xs">🇳🇪</span> Niger
          </span>
        </div>
      </header>

      {/* ── 3. CENTERED LIGHT FROSTED AUTH CARD (Apple/Amazon Style) ── */}
      <main className="relative z-10 w-full max-w-[440px] mx-auto px-4 py-8 sm:py-10">
        <div className="rounded-[32px] bg-white/85 sm:bg-white/90 backdrop-blur-2xl border border-white/80 shadow-[0_20px_70px_rgba(0,0,0,0.08)] p-6 sm:p-9 transition-all">

          {/* Segmented Apple-Style Mode Toggle */}
          <div className="mb-6 p-1 rounded-full bg-black/[0.05] border border-black/[0.05] flex items-center">
            <button
              type="button"
              onClick={() => switchMode("login")}
              className={`flex-1 h-9 rounded-full text-[13px] font-semibold transition-all duration-300 ease-[cubic-bezier(0.28,0.11,0.32,1)] ${
                isLogin
                  ? "bg-white text-[#1d1d1f] shadow-[0_2px_10px_rgba(0,0,0,0.08)] scale-[1.01] font-bold"
                  : "text-gray-500 hover:text-gray-900"
              }`}
            >
              {t("authPage.login", "Sign In")}
            </button>
            <button
              type="button"
              onClick={() => switchMode("signup")}
              className={`flex-1 h-9 rounded-full text-[13px] font-semibold transition-all duration-300 ease-[cubic-bezier(0.28,0.11,0.32,1)] ${
                !isLogin
                  ? "bg-white text-[#1d1d1f] shadow-[0_2px_10px_rgba(0,0,0,0.08)] scale-[1.01] font-bold"
                  : "text-gray-500 hover:text-gray-900"
              }`}
            >
              {t("authPage.signUp", "Create Account")}
            </button>
          </div>

          {/* Card Header */}
          <div className="mb-6 text-center">
            <h1 className="text-2xl sm:text-[26px] font-bold tracking-tight text-[#1d1d1f]">
              {pageTitle}
            </h1>
            <p className="mt-1 text-[13px] text-gray-500 leading-relaxed">
              {pageSubtitle}
            </p>
          </div>

          {/* Status / Error Banner */}
          {bannerMessage && (
            <div
              className={`mb-5 rounded-xl border p-3 flex items-start gap-2.5 text-[13px] leading-snug animate-fadeIn ${
                bannerIsError
                  ? "border-red-500/20 bg-red-50 text-red-700"
                  : "border-emerald-500/20 bg-emerald-50 text-emerald-700"
              }`}
            >
              <FiAlertCircle className="mt-0.5 shrink-0" size={16} />
              <p className="flex-1 font-medium">{bannerMessage}</p>
            </div>
          )}

          {/* Google One-Click Sign-In */}
          {!loading && (
            <div className="mb-5">
              <div className="w-full flex justify-center [&_button]:!w-full [&_button]:!rounded-full [&_button]:!h-11 [&_button]:!shadow-sm">
                <GoogleSignInButton
                  onSuccess={() => navigate(from)}
                  onError={(message) => setGoogleError(message)}
                  label={isLogin ? "Continue with Google" : "Sign up with Google"}
                />
              </div>

              {/* Minimal Divider */}
              <div className="relative my-5 flex items-center justify-center">
                <div className="w-full border-t border-black/[0.08]" />
                <span className="absolute px-3 bg-white/95 text-[11px] font-semibold uppercase tracking-widest text-gray-400">
                  {t("authPage.or", "or")}
                </span>
              </div>
            </div>
          )}

          {/* Credentials Form */}
          <form onSubmit={handleSubmit} className="space-y-3.5">
            {!isLogin && (
              <div className="grid grid-cols-2 gap-3">
                {/* First Name */}
                <div>
                  <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wider mb-1">
                    {t("authPage.firstName", "First Name")}
                  </label>
                  <div className="relative">
                    <FiUser
                      className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
                      size={15}
                    />
                    <input
                      type="text"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      className="w-full h-10 rounded-xl bg-white border border-black/[0.12] pl-9 pr-3 text-[13px] text-[#1d1d1f] placeholder:text-gray-400 outline-none transition-all focus:border-[var(--color-accent)] focus:ring-4 focus:ring-[var(--color-accent)]/15 shadow-sm"
                      required={!isLogin}
                    />
                  </div>
                  {renderFieldError("first_name")}
                </div>

                {/* Last Name */}
                <div>
                  <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wider mb-1">
                    {t("authPage.lastName", "Last Name")}
                  </label>
                  <div className="relative">
                    <FiUser
                      className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
                      size={15}
                    />
                    <input
                      type="text"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleInputChange}
                      className="w-full h-10 rounded-xl bg-white border border-black/[0.12] pl-9 pr-3 text-[13px] text-[#1d1d1f] placeholder:text-gray-400 outline-none transition-all focus:border-[var(--color-accent)] focus:ring-4 focus:ring-[var(--color-accent)]/15 shadow-sm"
                      required={!isLogin}
                    />
                  </div>
                  {renderFieldError("last_name")}
                </div>
              </div>
            )}

            {/* Phone Number (Sign Up Only) */}
            {!isLogin && (
              <div>
                <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wider mb-1">
                  {t("authPage.phoneNumber", "Phone Number (Optional)")}
                </label>
                <div className="relative">
                  <FiPhone
                    className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
                    size={15}
                  />
                  <input
                    type="tel"
                    name="phone_number"
                    placeholder="+227 90 00 00 00"
                    value={formData.phone_number}
                    onChange={handleInputChange}
                    className="w-full h-10 rounded-xl bg-white border border-black/[0.12] pl-9 pr-3 text-[13px] text-[#1d1d1f] placeholder:text-gray-400 outline-none transition-all focus:border-[var(--color-accent)] focus:ring-4 focus:ring-[var(--color-accent)]/15 shadow-sm"
                  />
                </div>
                {renderFieldError("phone_number")}
              </div>
            )}

            {/* Email Address */}
            <div>
              <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wider mb-1">
                {t("authPage.email", "Email Address")}
              </label>
              <div className="relative">
                <FiMail
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
                  size={15}
                />
                <input
                  type="email"
                  name="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full h-10 rounded-xl bg-white border border-black/[0.12] pl-9 pr-3 text-[13px] text-[#1d1d1f] placeholder:text-gray-400 outline-none transition-all focus:border-[var(--color-accent)] focus:ring-4 focus:ring-[var(--color-accent)]/15 shadow-sm"
                  required
                />
              </div>
              {renderFieldError("email")}
            </div>

            {/* Password */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wider">
                  {t("authPage.password", "Password")}
                </label>
                {isLogin && (
                  <button
                    type="button"
                    onClick={() => navigate("/forgot-password")}
                    className="text-[11px] font-semibold text-[var(--color-accent)] hover:underline transition-colors"
                  >
                    {t("authPage.forgotPassword", "Forgot password?")}
                  </button>
                )}
              </div>
              <div className="relative">
                <FiLock
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
                  size={15}
                />
                <input
                  type={showPassword ? "text" : "password"}
                  name="password1"
                  placeholder="••••••••"
                  value={formData.password1}
                  onChange={handleInputChange}
                  className="w-full h-10 rounded-xl bg-white border border-black/[0.12] pl-9 pr-10 text-[13px] text-[#1d1d1f] placeholder:text-gray-400 outline-none transition-all focus:border-[var(--color-accent)] focus:ring-4 focus:ring-[var(--color-accent)]/15 shadow-sm"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 transition-colors p-1"
                  aria-label="Toggle password visibility"
                >
                  {showPassword ? <FiEyeOff size={15} /> : <FiEye size={15} />}
                </button>
              </div>
              {renderFieldError("password1")}
            </div>

            {/* Confirm Password (Sign Up Only) */}
            {!isLogin && (
              <div>
                <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wider mb-1">
                  {t("authPage.confirmPassword", "Confirm Password")}
                </label>
                <div className="relative">
                  <FiLock
                    className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
                    size={15}
                  />
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    name="password2"
                    placeholder="••••••••"
                    value={formData.password2}
                    onChange={handleInputChange}
                    className="w-full h-10 rounded-xl bg-white border border-black/[0.12] pl-9 pr-10 text-[13px] text-[#1d1d1f] placeholder:text-gray-400 outline-none transition-all focus:border-[var(--color-accent)] focus:ring-4 focus:ring-[var(--color-accent)]/15 shadow-sm"
                    required={!isLogin}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 transition-colors p-1"
                    aria-label="Toggle confirm password visibility"
                  >
                    {showConfirmPassword ? <FiEyeOff size={15} /> : <FiEye size={15} />}
                  </button>
                </div>
                {renderFieldError("password2")}
              </div>
            )}

            {/* Login Remember Me Checkbox */}
            {isLogin && (
              <div className="pt-0.5">
                <label className="flex items-center gap-2 cursor-pointer select-none text-[12px] text-gray-600 hover:text-gray-900 transition-colors">
                  <input
                    type="checkbox"
                    className="w-3.5 h-3.5 rounded border-gray-300 text-[var(--color-accent)] focus:ring-[var(--color-accent)] cursor-pointer"
                  />
                  <span>{t("authPage.rememberMe", "Keep me signed in on this device")}</span>
                </label>
              </div>
            )}

            {/* Sign Up Terms Checkbox */}
            {!isLogin && (
              <div className="pt-0.5 space-y-1">
                <label className="flex items-start gap-2.5 cursor-pointer select-none text-[12px] text-gray-600">
                  <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    className="mt-0.5 w-3.5 h-3.5 rounded border-gray-300 text-[var(--color-accent)] focus:ring-[var(--color-accent)] cursor-pointer"
                  />
                  <span className="leading-snug text-gray-600">
                    {t("authPage.agreeTermsPrefix", "I agree to the")}{" "}
                    <Link
                      to="/terms-of-service"
                      target="_blank"
                      className="text-[var(--color-accent)] font-semibold hover:underline"
                    >
                      {t("authPage.termsOfUse", "Terms")}
                    </Link>{" "}
                    &{" "}
                    <Link
                      to="/privacy-policy"
                      target="_blank"
                      className="text-[var(--color-accent)] font-semibold hover:underline"
                    >
                      {t("footer.privacyPolicy", "Privacy Policy")}
                    </Link>
                  </span>
                </label>
                {renderFieldError("terms")}
              </div>
            )}

            {/* Primary Action Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full h-11 mt-3 rounded-full bg-gradient-to-r from-[#ff9f0a] to-[#ff5e00] text-white font-bold text-[14px] shadow-[0_6px_22px_rgba(255,106,0,0.38)] hover:shadow-[0_8px_28px_rgba(255,106,0,0.48)] hover:scale-[1.01] active:scale-[0.99] transition-all duration-200 flex items-center justify-center gap-2"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <span>{isLogin ? t("authPage.login", "Sign In") : t("authPage.signUp", "Create Account")}</span>
                  <FiArrowRight size={16} />
                </>
              )}
            </button>
          </form>

          {/* Alternative Switch Prompt */}
          <div className="mt-6 pt-5 border-t border-black/[0.06] text-center text-[13px] text-gray-500">
            <span>
              {isLogin
                ? t("authPage.noAccount", "New to Chafadi?")
                : t("authPage.haveAccount", "Already have an account?")}
            </span>{" "}
            <button
              type="button"
              onClick={() => switchMode(isLogin ? "signup" : "login")}
              className="font-bold text-[var(--color-accent)] hover:underline ml-1 transition-colors"
            >
              {isLogin ? t("authPage.signUp", "Create your Chafadi account") : t("authPage.login", "Sign in here")}
            </button>
          </div>
        </div>

        {/* ── TRUST BADGES ── */}
        <div className="mt-6 flex items-center justify-center gap-6 text-[11px] text-gray-700">
          <span className="flex items-center gap-1.5 font-medium px-3 py-1 rounded-full bg-white/85 backdrop-blur-md border border-black/[0.06] shadow-sm">
            <FiShield className="text-[var(--color-accent)]" size={13} />
            <span>Niger SSL Encryption</span>
          </span>
          <span className="flex items-center gap-1.5 font-medium px-3 py-1 rounded-full bg-white/85 backdrop-blur-md border border-black/[0.06] shadow-sm">
            <FiCheck className="text-[var(--color-accent)]" size={13} />
            <span>Verified Local Sellers</span>
          </span>
        </div>
      </main>

      {/* ── FROSTED FOOTER ── */}
      <footer className="relative z-10 w-full max-w-4xl mx-auto px-4 pb-6 pt-2 text-center text-gray-600">
        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[12px]">
          <Link to="/terms-of-service" className="hover:text-gray-900 transition-colors">
            {t("authPage.termsOfUse", "Terms of Service")}
          </Link>
          <span>•</span>
          <Link to="/privacy-policy" className="hover:text-gray-900 transition-colors">
            {t("footer.privacyPolicy", "Privacy Policy")}
          </Link>
          <span>•</span>
          <Link to="/about" className="hover:text-gray-900 transition-colors">
            {t("navbar.about", "About Us")}
          </Link>
          <span>•</span>
          <a
            href="https://wa.me/22790000000"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline text-[var(--color-accent)] font-semibold"
          >
            WhatsApp Support
          </a>
        </div>
        <p className="mt-2 text-[11px] text-gray-400">
          © 2026 Chafadi Inc. All rights reserved. Niamey, Niger.
        </p>
      </footer>
    </div>
  );
};

export default AuthPage;
